import { FC, useState } from 'react'
import { ReactComponent as FacebookIcon } from 'src/assets/svg/facebook-fill.svg'
import { ReactComponent as InfoIcon } from 'src/assets/svg/ri-information-fill.svg'
import { ReactComponent as InstagramIcon } from 'src/assets/svg/instagram-fill.svg'
import { ReactComponent as TiktokIcon } from 'src/assets/svg/tiktok-fill.svg'
import { TruncatedTooltip } from 'src/components/TruncatedTooltip'
import { campaignColor } from 'src/pages/Campaigns/CampaignComparison/MultiComparison/palette'
import styles from './publishingIntensityMap.module.scss'
import { ContentCampaign, IntensityDay, IntensityRow } from '../interfaces'

interface PublishingIntensityMapProps {
  campaigns: ContentCampaign[]
  dayCount: number
  endLabel: string
  midLabel: string
  rows: IntensityRow[]
  startLabel: string
}

const COPY = {
  comments: 'Comments',
  footnote: "Hover any cell for that day's exact post and comment count.",
  platforms: 'Platforms',
  posts: 'Posts',
  subtitlePrefix: 'Activity timeline across a',
  title: 'Campaign Publishing Intensity Map',
  total: 'Total',
}

const PLATFORM_ICONS: Record<string, FC<{ className?: string }>> = {
  Facebook: FacebookIcon,
  Instagram: InstagramIcon,
  TikTok: TiktokIcon,
}

// Which cell the pointer is over. The tooltip is one node at the widget's
// level rather than one per cell — at four campaigns across 45 days that is
// 180 cells, and 180 mounted tooltips is what made this stutter.
interface Hovered {
  campaignIndex: number
  day: IntensityDay
}

/*
 * Where the tooltip sits, in the grid's own coordinates.
 *
 * The measurements are the stylesheet's, restated: the grid's padding, the
 * name column and the gap after it, then an equal share per day. Reading them
 * back off the DOM would be truer, but it costs a layout read on every cell
 * the pointer crosses, and these do not move.
 */
const GRID_PADDING = 12
const NAME_WIDTH = 100
const NAME_GAP = 14
const CARD_WIDTH = 1170
const CARD_PADDING = 24
const TOOLTIP_WIDTH = 340
const EDGE = 8

// Row geometry, so the tooltip rises from the row being pointed at rather
// than from a fixed height that only suited the first one.
const ROW_HEIGHT = 26
const ROW_GAP = 8

// The gap the design leaves between the tooltip's tail and the dot it points
// at. The dot itself is not decorated on hover.
const DOT_CLEARANCE = 24

const GRID_WIDTH = CARD_WIDTH - CARD_PADDING * 2
const CELLS_LEFT = GRID_PADDING + NAME_WIDTH + NAME_GAP
const CELLS_WIDTH = GRID_WIDTH - CELLS_LEFT - GRID_PADDING

// The centre of a day's cell. Cells are spread with space-between, so the
// first and last sit half a cell inside the track.
const cellCentre = (day: number, dayCount: number) => {
  const half = CELLS_WIDTH / dayCount / 2

  return CELLS_LEFT + half + (day / (dayCount - 1)) * (CELLS_WIDTH - half * 2)
}

// Kept clear of the card's edges, so a day near either end does not print a
// tooltip half off the card.
const clampCentre = (centre: number) =>
  Math.min(
    Math.max(centre, TOOLTIP_WIDTH / 2 + EDGE),
    GRID_WIDTH - TOOLTIP_WIDTH / 2 - EDGE
  )

// The top of the tooltip's tail: clear of the dot it is describing.
const rowTop = (campaignIndex: number) =>
  GRID_PADDING +
  campaignIndex * (ROW_HEIGHT + ROW_GAP) +
  ROW_HEIGHT / 2 -
  DOT_CLEARANCE

export const PublishingIntensityMap: FC<PublishingIntensityMapProps> = ({
  campaigns,
  dayCount,
  endLabel,
  midLabel,
  rows,
  startLabel,
}) => {
  const [hovered, setHovered] = useState<Hovered | null>(null)

  const centre = hovered ? cellCentre(hovered.day.day, dayCount) : 0
  const anchored = clampCentre(centre)

  const totals = hovered
    ? hovered.day.platforms.reduce(
        (sum, platform) => ({
          comments: sum.comments + platform.comments,
          posts: sum.posts + platform.posts,
        }),
        { comments: 0, posts: 0 }
      )
    : null

  return (
    <section className={styles.card}>
      <div className={styles.headerText}>
        <h3 className={styles.title}>{COPY.title}</h3>
        <p className={styles.subtitle}>
          {`${COPY.subtitlePrefix} ${startLabel} – ${endLabel} 2026`}
        </p>
      </div>

      <div className={styles.scale}>
        <span className={styles.scaleStart}>{startLabel}</span>
        <span className={styles.scaleMid}>{midLabel}</span>
        <span className={styles.scaleEnd}>{endLabel}</span>
      </div>

      <div className={styles.grid}>
        {campaigns.map((campaign, campaignIndex) => {
          const row = rows.find((item) => item.campaignId === campaign.id)
          const byDay = new Map(row?.days.map((day) => [day.day, day]) ?? [])

          return (
            <div className={styles.gridRow} key={campaign.id}>
              <TruncatedTooltip
                className={styles.rowName}
                maxLines={2}
                text={campaign.name}
              />

              <div className={styles.cells}>
                {Array.from({ length: dayCount }, (unused, day) => {
                  const active = byDay.get(day)

                  return (
                    <span
                      className={styles.cell}
                      key={day}
                      onMouseEnter={() =>
                        active && setHovered({ campaignIndex, day: active })
                      }
                      onMouseLeave={() => setHovered(null)}
                    >
                      <span
                        className={active ? styles.dotActive : styles.dotEmpty}
                        style={
                          active
                            ? { background: campaignColor(campaignIndex) }
                            : undefined
                        }
                      />
                    </span>
                  )
                })}
              </div>
            </div>
          )
        })}

        {hovered && totals && (
          <div
            className={styles.tooltip}
            style={{
              left: `${anchored}px`,
              top: `${rowTop(hovered.campaignIndex)}px`,
            }}
          >
            {/* Points back at the cell, not at the tooltip, so a clamped
                tooltip still names which day it is describing. */}
            <span
              className={styles.tooltipTail}
              style={{ left: `${centre - anchored + TOOLTIP_WIDTH / 2}px` }}
            />

            <p className={styles.tooltipDate}>{hovered.day.label}</p>

            <p className={styles.tooltipLabel}>{COPY.platforms}</p>

            <ul className={styles.tooltipRows}>
              {hovered.day.platforms.map((platform) => {
                const Icon = PLATFORM_ICONS[platform.name]

                return (
                  <li className={styles.tooltipRow} key={platform.name}>
                    <span className={styles.tooltipPlatform}>
                      {Icon && <Icon className={styles.tooltipIcon} />}
                      {platform.name}
                    </span>

                    <span className={styles.tooltipStat}>
                      {COPY.posts}
                      <b className={styles.tooltipValue}>{platform.posts}</b>
                    </span>

                    <span className={styles.tooltipStat}>
                      {COPY.comments}
                      <b className={styles.tooltipValue}>{platform.comments}</b>
                    </span>
                  </li>
                )
              })}
            </ul>

            <div className={styles.tooltipTotal}>
              <span className={styles.tooltipTotalLabel}>{COPY.total}</span>

              <span className={styles.tooltipStat}>
                {COPY.posts}
                <b className={styles.tooltipValue}>{totals.posts}</b>
              </span>

              <span className={styles.tooltipStat}>
                {COPY.comments}
                <b className={styles.tooltipValue}>{totals.comments}</b>
              </span>
            </div>
          </div>
        )}
      </div>

      <ul className={styles.legend}>
        {campaigns.map((campaign, index) => (
          <li className={styles.legendItem} key={campaign.id}>
            <span
              className={styles.legendDot}
              style={{ background: campaignColor(index) }}
            />
            {campaign.name}
          </li>
        ))}
      </ul>

      <p className={styles.footnote}>
        <InfoIcon className={styles.footnoteIcon} />
        {COPY.footnote}
      </p>
    </section>
  )
}
